import Datatable from "../../Components/Datatable";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../../context/AuthContext";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_BASE_URL } from "../../../../../Helpers/apiConfig";

export const AdminInactiveUserList = () => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [users, setUsers] = useState([]);
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState("");

    const [selectedSchoolId, setSelectedSchoolId] = useState("all");
    const [userScope, setUserScope] = useState("all"); // all | individual | corporate
    const [inactiveRange, setInactiveRange] = useState("7"); // 7 | 30

    const formatDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toLocaleString("tr-TR");
    };

    const calculateDaysSinceLogin = (lastLogin) => {
        if (!lastLogin) return null;
        const lastLoginDate = new Date(lastLogin);
        if (Number.isNaN(lastLoginDate.getTime())) return null;

        const now = new Date();
        const diffMs = now.getTime() - lastLoginDate.getTime();
        return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    };

    const getUserTypeLabel = (item) => {
        if (item?.user?.isInvidual) return "Bireysel";
        return "Kurumsal";
    };

    const getSchoolLabel = (item) => {
        if (item?.detail?.schoolName) return item.detail.schoolName;
        if (item?.detail?.schoolId) return `Okul #${item.detail.schoolId}`;
        return "Bireysel / Okulsuz";
    };

    const columns = useMemo(
        () => [
            {
                Header: t("namesurname"),
                Footer: t("namesurname"),
                accessor: "name",
            },
            {
                Header: t("email"),
                Footer: t("email"),
                accessor: "email",
            },
            {
                Header: t("schoolName"),
                Footer: t("schoolName"),
                accessor: "schoolName",
            },
            {
                Header: t("sectionName"),
                Footer: t("sectionName"),
                accessor: "sectionName",
            },

            {
                Header: "Son Giriş",
                Footer: "Son Giriş",
                accessor: "lastLogin",
            },
            {
                Header: "Girmeyeli (Gün)",
                Footer: "Girmeyeli (Gün)",
                accessor: "daysSinceLogin",
            },
        ],
        [t]
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setMsg("");

            try {
                const headers = {
                    accept: "*/*",
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Credentials": "false",
                    Authorization: "Bearer " + user.token,
                };

                const [usersResponse, schoolsResponse] = await Promise.all([
                    axios({
                        method: "GET",
                        url: `${API_BASE_URL}/admin/User/GetUsers`,
                        headers,
                    }),
                    axios({
                        method: "GET",
                        url: `${API_BASE_URL}/admin/School/SchoolList`,
                        headers,
                    }),
                ]);

                if (usersResponse.status === 200 && usersResponse.data?.success) {
                    const mappedUsers = (usersResponse.data.data || []).map((item) => {
                        const daysSinceLogin = calculateDaysSinceLogin(item?.user?.lastLogin);

                        return {
                            id: item?.user?.id,
                            name: item?.user?.fullName || "-",
                            email: item?.user?.username || "-",
                            roleName: item?.user?.roleName || "-",
                            schoolId: item?.detail?.schoolId ?? "-",
                            schoolName: getSchoolLabel(item),
                            sectionName: item?.detail?.sectionName || "-",
                            userType: getUserTypeLabel(item),
                            lastLogin: formatDate(item?.user?.lastLogin),
                            lastLoginRaw: item?.user?.lastLogin || null,
                            daysSinceLogin: daysSinceLogin === null ? "Hiç giriş yapmamış" : daysSinceLogin,
                            daysSinceLoginRaw: daysSinceLogin,
                            isInvidual: !!item?.user?.isInvidual,
                            isStudent: !!item?.user?.isStudent,
                            rawSchoolId: item?.detail?.schoolId,
                        };
                    });

                    setUsers(mappedUsers);
                } else {
                    setMsg(usersResponse.data?.message || "Kullanıcılar alınırken hata oluştu.");
                }

                if (schoolsResponse.status === 200 && schoolsResponse.data?.success) {
                    const mappedSchools = (schoolsResponse.data.data || [])
                        .filter((school) => !school?.isDeleted)
                        .map((school) => ({
                            id: school.id,
                            schoolName: school.schoolName,
                        }));

                    setSchools(mappedSchools);
                }
            } catch (error) {
                setMsg(error?.message || "Beklenmedik bir hata oluştu.");
            }

            setLoading(false);
        };

        fetchData();
    }, [user.token]);

    const filteredUsers = useMemo(() => {
        const dayLimit = Number(inactiveRange);

        return users.filter((item) => {
            const inactiveMatch =
                item.daysSinceLoginRaw === null || item.daysSinceLoginRaw >= dayLimit;

            if (userScope === "individual") {
                return item.isInvidual && inactiveMatch;
            }

            if (userScope === "corporate") {
                const corporateSchoolMatch =
                    selectedSchoolId === "all"
                        ? true
                        : String(item.rawSchoolId) === String(selectedSchoolId);

                return !item.isInvidual && corporateSchoolMatch && inactiveMatch;
            }

            const schoolMatch =
                selectedSchoolId === "all"
                    ? true
                    : String(item.rawSchoolId) === String(selectedSchoolId);

            return schoolMatch && inactiveMatch;
        });
    }, [users, selectedSchoolId, userScope, inactiveRange]);

    const neverLoggedInCount = useMemo(() => {
        return filteredUsers.filter((item) => item.daysSinceLoginRaw === null).length;
    }, [filteredUsers]);

    const exportToExcel = () => {
        const dataToExport = filteredUsers.map((item) => ({
            ID: item.id,
            "Ad Soyad": item.name,
            Email: item.email,
            "Okul Adı": item.schoolName,
            "Oluşturulma Tarihi": item.createdAt,
            "Son Giriş": item.lastLogin,
            "Girmediği Gün": item.daysSinceLogin
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "GirisYapmayanKullanicilar");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const fileData = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        });

        saveAs(fileData, "GirisYapmayanKullanicilar.xlsx");
    };

    return (
        <div>
            <div style={{ textAlign: "center" }}>
                <h1>Giriş Yapmayan Kullanıcılar</h1>
                <p style={{ marginBottom: "20px" }}>
                    Son 7 gün veya 30 gün içinde giriş yapmayan kullanıcıları okul bazlı,
                    bireysel veya tüm kullanıcılar için filtreleyebilirsiniz.
                </p>
                <h4>{msg}</h4>
            </div>

            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label"><b>Okul</b></label>
                            <select
                                className="form-select"
                                value={selectedSchoolId}
                                onChange={(e) => setSelectedSchoolId(e.target.value)}
                            >
                                <option value="all">Tüm Okullar</option>
                                {schools.map((school) => (
                                    <option key={school.id} value={school.id}>
                                        {school.schoolName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="form-label"><b>Kullanıcı Kapsamı</b></label>
                            <select
                                className="form-select"
                                value={userScope}
                                onChange={(e) => setUserScope(e.target.value)}
                            >
                                <option value="all">Tüm Kullanıcılar</option>
                                <option value="individual">Sadece Bireysel</option>
                                <option value="corporate">Sadece Kurumsal</option>
                            </select>
                        </div>

                        <div className="col-md-4 mb-3">
                            <label className="form-label"><b>Giriş Yapmama Süresi</b></label>
                            <select
                                className="form-select"
                                value={inactiveRange}
                                onChange={(e) => setInactiveRange(e.target.value)}
                            >
                                <option value="7">Son 7 gün içinde girmeyenler</option>
                                <option value="30">Son 30 gün içinde girmeyenler</option>
                            </select>
                        </div>
                    </div>

                    <div className="row mt-2">
                        <div className="col-md-4 mb-2">
                            <div className="alert alert-light border mb-0">
                                <b>Toplam Kayıt:</b> {filteredUsers.length}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="alert alert-light border mb-0">
                                <b>Hiç Giriş Yapmamış:</b> {neverLoggedInCount}
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="alert alert-light border mb-0">
                                <b>Aktif Filtre:</b>{" "}
                                {inactiveRange === "7" ? "7 Gün" : "30 Gün"} /{" "}
                                {userScope === "all"
                                    ? "Tümü"
                                    : userScope === "individual"
                                        ? "Bireysel"
                                        : "Kurumsal"}
                            </div>
                        </div>
                    </div>

                    <div className="row mt-3">
                        <div className="col-md-12">
                            <button
                                className="btn btn-success"
                                onClick={exportToExcel}
                                disabled={filteredUsers.length === 0}
                            >
                                Excel İndir
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {loading && <div>Veriler yükleniyor...</div>}

            {!loading && (
                filteredUsers.length === 0 ? (
                    <div className="alert alert-warning text-center">
                        Kayıt bulunamadı
                    </div>
                ) : (
                    <Datatable
                        data={filteredUsers}
                        columns={columns}
                        tableBaslik="Giriş Yapmayan Kullanıcılar"
                    />
                )
            )}
        </div>
    );
};

export default AdminInactiveUserList;